// Хук для вычисления позиций транспорта на карте истории через Web Worker
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
// worker-loader: classic worker script without ES module exports
// @ts-expect-error worker module is provided by worker-loader at build time
import HistoryPositionsWorkerModule from '../workers/historyPositions.worker.ts';
const HistoryPositionsWorker = HistoryPositionsWorkerModule;
const INTERVAL_SECONDS = 30 * 60;
/**
 * Хук для вычисления позиций транспорта через Web Worker
 * @returns {Object} - { positions, isComputing, error }
 */
export function useHistoryPositions() {
    const { isHistoryOpen, historySelectedTime, transportHistoryTracksItems, transportCheckedItems, transportItems, historyTracksLoading, historyTracksProgress } = useSelector((state) => state.map);
    const prevLoadingRef = useRef(null);
    // Мемоизируем хеш данных для стабильной ссылки
    const tracksItemsHash = useMemo(() => {
        if (!transportHistoryTracksItems)
            return 'empty';
        const keys = Object.keys(transportHistoryTracksItems).sort();
        const hash = keys.map((k) => `${k}:${Object.keys(transportHistoryTracksItems[k] || {}).length}`).join(';');
        // Подсчитываем общее количество загруженных транспортов
        const loadedTransports = new Set();
        for (const transportId of keys) {
            loadedTransports.add(transportId);
        }
        // Логируем изменение хеша
        console.log('[tracksItemsHash] Updated:', {
            transportsCount: loadedTransports.size,
            expectedTransports: (transportCheckedItems === null || transportCheckedItems === void 0 ? void 0 : transportCheckedItems.length) || 0,
            intervalsCount: keys.length,
            hash: hash.substring(0, 100) + '...',
        });
        return hash;
    }, [transportHistoryTracksItems, transportCheckedItems === null || transportCheckedItems === void 0 ? void 0 : transportCheckedItems.length]);
    const workerRef = useRef(null);
    const requestIdRef = useRef(0);
    const debounceTimerRef = useRef(null);
    const forceUpdateTimerRef = useRef(null);
    const lastComputeTimeRef = useRef(0);
    const [positions, setPositions] = useState([]);
    const [isComputing, setIsComputing] = useState(false);
    const [error, setError] = useState(null);
    // Инициализация воркера
    useEffect(() => {
        workerRef.current = new HistoryPositionsWorker();
        workerRef.current.onmessage = (e) => {
            const { type, requestId, positions: result, error: err } = e.data;
            if (type === 'result') {
                if (requestId === requestIdRef.current) {
                    setPositions(result);
                    setIsComputing(false);
                    setError(null);
                }
            }
            else if (type === 'error') {
                setError(err);
                setIsComputing(false);
            }
        };
        workerRef.current.onerror = (err) => {
            setError(err.message);
            setIsComputing(false);
        };
        // Инициализируем воркер
        workerRef.current.postMessage({ type: 'init', requestId: ++requestIdRef.current });
        // Очистка при размонтировании
        return () => {
            if (workerRef.current) {
                workerRef.current.terminate();
                workerRef.current = null;
            }
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);
    // Функция для отправки запроса на вычисление
    const computePositions = useCallback((targetTime, transportIds, tracksData) => {
        console.log('[computePositions] Called:', {
            hasWorker: !!workerRef.current,
            isHistoryOpen,
            targetTime,
            transportIdsLength: transportIds === null || transportIds === void 0 ? void 0 : transportIds.length,
            tracksDataType: typeof tracksData,
            tracksDataKeys: tracksData ? Object.keys(tracksData) : 'none',
        });
        if (!workerRef.current || !isHistoryOpen || !targetTime || !transportIds || transportIds.length === 0) {
            console.log('[computePositions] Skipped - invalid params');
            setPositions([]);
            setIsComputing(false);
            return;
        }
        // Вычисляем целевой интервал
        const historyTimeUnix = Math.floor(moment(targetTime).valueOf() / 1000);
        const targetIntervalKey = String(Math.floor(historyTimeUnix / INTERVAL_SECONDS));
        const targetTimestamp = historyTimeUnix;
        // Подготавливаем информацию о транспортах
        const transportInfo = {};
        if (transportItems && Array.isArray(transportItems)) {
            for (const transport of transportItems) {
                if (transport && transport.id) {
                    transportInfo[transport.id] = {
                        name: transport.name || '',
                        color: transport.color || '#000000',
                    };
                }
            }
        }
        // Фильтруем tracksData только для нужных транспортов
        // Данные в store: transportHistoryTracksItems = { [transportId]: { [intervalKey]: data } }
        const filteredTracksData = {};
        console.log('[computePositions] Debug input:', {
            tracksDataTransportIds: tracksData ? Object.keys(tracksData).slice(0, 5) : 'none',
            transportIdsLength: transportIds === null || transportIds === void 0 ? void 0 : transportIds.length,
            transportIdsSample: transportIds === null || transportIds === void 0 ? void 0 : transportIds.slice(0, 5),
        });
        if (tracksData && transportIds) {
            // Создаём Set для быстрого поиска
            const transportIdsSet = new Set(transportIds.map((id) => String(id)));
            for (const transportId of Object.keys(tracksData)) {
                // Проверяем, нужен ли этот транспорт
                if (transportIdsSet.has(String(transportId))) {
                    filteredTracksData[transportId] = tracksData[transportId];
                }
            }
        }
        console.log('[computePositions] Filtering result:', {
            inputTransportIds: tracksData ? Object.keys(tracksData).length : 0,
            transportIdsCount: transportIds === null || transportIds === void 0 ? void 0 : transportIds.length,
            outputTransports: Object.keys(filteredTracksData).length,
        });
        setIsComputing(true);
        setError(null);
        const requestId = ++requestIdRef.current;
        workerRef.current.postMessage({
            type: 'compute',
            requestId,
            params: {
                targetTimestamp,
                transportIds,
                transportInfo,
                tracksData: filteredTracksData,
                targetIntervalKey,
            },
        });
    }, [isHistoryOpen, transportItems]);
    // Debug: логируем изменение historyTracksLoading и управляем кэшем worker'а
    useEffect(() => {
        if (prevLoadingRef.current !== null && prevLoadingRef.current !== historyTracksLoading) {
            console.log('[useHistoryPositions] historyTracksLoading changed:', prevLoadingRef.current, '->', historyTracksLoading);
            // Когда начинается загрузка (true) - очищаем кэш worker'а
            if (prevLoadingRef.current === false && historyTracksLoading === true) {
                console.log('[useHistoryPositions] Loading started - clearing worker cache');
                if (workerRef.current) {
                    workerRef.current.postMessage({ type: 'clearCache', requestId: ++requestIdRef.current });
                }
            }
            // Когда загрузка завершается (false), принудительно запускаем пересчёт
            if (prevLoadingRef.current === true && historyTracksLoading === false) {
                console.log('[useHistoryPositions] Loading complete - triggering compute');
                // Используем setTimeout чтобы данные успели обновиться в store
                setTimeout(() => {
                    computePositions(historySelectedTime, transportCheckedItems, transportHistoryTracksItems);
                }, 100);
            }
        }
        prevLoadingRef.current = historyTracksLoading;
    }, [historyTracksLoading, computePositions, historySelectedTime, transportCheckedItems, transportHistoryTracksItems]);
    // Debounced вычисление позиций + принудительный пересчёт каждые 3 секунды при загрузке
    useEffect(() => {
        // Очищаем предыдущий таймер
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        // Очищаем таймер принудительного обновления
        if (forceUpdateTimerRef.current) {
            clearInterval(forceUpdateTimerRef.current);
            forceUpdateTimerRef.current = null;
        }
        // Подсчитываем количество загруженных транспортов
        const loadedTransportIds = Object.keys(transportHistoryTracksItems || {});
        const loadedCount = loadedTransportIds.length;
        const expectedCount = (transportCheckedItems === null || transportCheckedItems === void 0 ? void 0 : transportCheckedItems.length) || 0;
        const allLoaded = loadedCount >= expectedCount && expectedCount > 0;
        console.log('[useHistoryPositions] useEffect triggered:', {
            isHistoryOpen,
            transportCheckedItemsLength: transportCheckedItems === null || transportCheckedItems === void 0 ? void 0 : transportCheckedItems.length,
            loadedTransports: loadedCount,
            expectedTransports: expectedCount,
            allLoaded,
            historyTracksLoading,
            progress: historyTracksProgress,
        });
        if (!isHistoryOpen || !transportCheckedItems || transportCheckedItems.length === 0) {
            setPositions([]);
            setIsComputing(false);
            return;
        }
        // Если идёт загрузка И ещё не все транспорты загружены - запускаем периодический пересчёт
        if (historyTracksLoading && !allLoaded) {
            console.log('[useHistoryPositions] Loading in progress, loaded', loadedCount, '/', expectedCount, 'transports');
            // Запускаем вычисление с текущими данными (для отображения прогресса)
            const doCompute = () => {
                const now = Date.now();
                if (now - lastComputeTimeRef.current >= 500) {
                    lastComputeTimeRef.current = now;
                    computePositions(historySelectedTime, transportCheckedItems, transportHistoryTracksItems);
                }
            };
            // Начальный расчёт с задержкой
            debounceTimerRef.current = setTimeout(() => {
                doCompute();
            }, 150);
            // Принудительный пересчёт каждые 2 секунды пока идёт загрузка
            forceUpdateTimerRef.current = setInterval(() => {
                if (isHistoryOpen && (transportCheckedItems === null || transportCheckedItems === void 0 ? void 0 : transportCheckedItems.length) > 0) {
                    doCompute();
                }
            }, 2000);
            return () => {
                if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                }
                if (forceUpdateTimerRef.current) {
                    clearInterval(forceUpdateTimerRef.current);
                    forceUpdateTimerRef.current = null;
                }
            };
        }
        // Все транспорты загружены или загрузка завершена - делаем финальный расчёт
        console.log('[useHistoryPositions] All transports loaded or loading complete, computing final positions');
        const doCompute = () => {
            var _a;
            const now = Date.now();
            if (now - lastComputeTimeRef.current >= 300) {
                lastComputeTimeRef.current = now;
                // Логируем состояние данных перед вычислением
                const trackKeys = Object.keys(transportHistoryTracksItems || {});
                let totalPoints = 0;
                for (const transportId of trackKeys) {
                    const intervals = transportHistoryTracksItems[transportId];
                    if (intervals) {
                        for (const intervalKey of Object.keys(intervals)) {
                            if ((_a = intervals[intervalKey]) === null || _a === void 0 ? void 0 : _a.pointsCount) {
                                totalPoints += intervals[intervalKey].pointsCount;
                            }
                        }
                    }
                }
                console.log('[useHistoryPositions] Computing positions:', {
                    transports: trackKeys.length,
                    totalPoints,
                    historySelectedTime,
                });
                computePositions(historySelectedTime, transportCheckedItems, transportHistoryTracksItems);
            }
        };
        // Debounce 150ms - ждём пока пользователь перестанет двигать слайдер
        debounceTimerRef.current = setTimeout(() => {
            doCompute();
        }, 150);
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [isHistoryOpen, historySelectedTime, transportCheckedItems, tracksItemsHash, historyTracksLoading, historyTracksProgress, computePositions]);
    // Функция для очистки кэша
    const clearCache = useCallback(() => {
        if (workerRef.current) {
            workerRef.current.postMessage({ type: 'clearCache', requestId: ++requestIdRef.current });
        }
    }, []);
    return {
        positions,
        isComputing,
        error,
        clearCache,
        computePositions, // Для ручного запуска вычисления
    };
}
export default useHistoryPositions;
